import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { ELocalStorage } from '../../enums';

export type ThemeMode = 'light' | 'dark' | 'system';

const MODES: ThemeMode[] = ['light', 'dark', 'system'];

// Default for a browser that has never picked a theme. Dark keeps the look existing
// users already know; switch to 'system' to follow the OS for first-time visitors.
// Keep in sync with the boot script in index.html.
export const DEFAULT_THEME_MODE: ThemeMode = 'dark';

// Light / dark / follow-the-OS theme switch. Dark is signalled by the `p-dark` class on
// <html>: PrimeNG's darkModeSelector (app.config.ts) and the light-theme token overrides
// in styles.css both key off it. index.html applies the stored choice before Angular
// boots so the page never flashes the wrong theme; this service takes over from there.
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject(DOCUMENT);
  // Asks "is the OS light?" rather than "is it dark?" so an OS with no preference
  // resolves to dark, the app's original look. Matches the index.html boot script.
  private media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: light)') ?? null;
  private systemDark = signal(!(this.media?.matches ?? false));

  readonly mode = signal<ThemeMode>(this.readStored());
  readonly isDark = computed(() => {
    const mode = this.mode();
    return mode === 'dark' || (mode === 'system' && this.systemDark());
  });

  constructor() {
    this.media?.addEventListener?.('change', (e) => {
      this.systemDark.set(!e.matches);
      this.apply();
    });
    this.apply();
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    try {
      localStorage.setItem(ELocalStorage.THEME, mode);
    } catch {
      // Storage blocked (private mode etc.) — the choice still applies for this session.
    }
    this.apply();
  }

  private apply(): void {
    const root = this.document.documentElement;
    const dark = this.isDark();
    root.classList.toggle('p-dark', dark);
    root.style.colorScheme = dark ? 'dark' : 'light';
  }

  private readStored(): ThemeMode {
    try {
      const stored = localStorage.getItem(ELocalStorage.THEME) as ThemeMode | null;
      if (stored && MODES.includes(stored)) return stored;
    } catch {
      // fall through to the default
    }
    return DEFAULT_THEME_MODE;
  }
}
